import { observer } from 'mobx-react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import React, { useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import fallback from '../../../_Translations/generated/session-time-out-modal.en.t.json';
import Keys from '../../../_Translations/generated/session-time-out-modal.en.t.json.keys';
import UseLoginService from '../../Hooks/useLoginService';
import useAuth from '../../../ISO/Hooks/useAuth';
import useTranslation from '../../../ISO/Hooks/useTranslation';
import useWindow from '../../../ISO/Hooks/useWindow';
import { LoggerFactory } from '@ibe/services';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = LoggerFactory.get('SessionTimeOutModal');
interface Props {
  sessionTimeOut: number;
  sessionTimeOutNotificationOffset: number;
  silentRefreshThrottle?: number;
  allowUnauthorizedUser?: boolean;
  onLogoutRedirection: () => void;
}

const MAX_TIME_FOR_SILENT_REFRESH = 60_000;
const DEFAULT_THROTTLE = 500;

const SessionTimeOutModal = observer((props: Props) => {
  const window = useWindow();
  const { t } = useTranslation('session-time-out-modal', fallback, Keys);
  const loginService = UseLoginService();
  const auth = useAuth();
  const {
    silentRefreshThrottle = undefined,
    sessionTimeOut,
    sessionTimeOutNotificationOffset,
    onLogoutRedirection,
    allowUnauthorizedUser
  } = props;
  const [sessionTimeOutModalOpen, setSessionTimeOutModalOpen] = useState(false);
  const [sessionTimeOutNotification, setSessionTimeOutNotification] = useState(false);

  const silentRefreshToken = (): void => {
    const { authService } = auth;
    if (
      (auth.authState.isLoggedIn || allowUnauthorizedUser) &&
      !sessionTimeOutModalOpen &&
      !sessionTimeOutNotification &&
      !isIdle
    ) {
      const expiration = authService.getRemainingTokenTime();
      if (expiration && expiration > 0 && expiration < MAX_TIME_FOR_SILENT_REFRESH) {
        loginService
          .refreshToken()
          .then(() => auth.persistToken())
          .catch(() => toggleSessionExpiredModal());
      }
    }
  };

  const onIdle = (): void => {
    if (auth.authState.isLoggedIn || allowUnauthorizedUser) {
      pause();
      setSessionTimeOutNotification(false);
      setSessionTimeOutModalOpen(true);
    }
  };

  const onPrompt = (): void => {
    if (auth.authState.isLoggedIn || allowUnauthorizedUser) {
      setSessionTimeOutNotification(true);
    }
  };

  const { activate, isIdle, pause } = useIdleTimer({
    onIdle,
    onAction: silentRefreshThrottle ? silentRefreshToken : () => {},
    onPrompt,
    timeout: sessionTimeOut,
    promptBeforeIdle: sessionTimeOutNotificationOffset,
    throttle: silentRefreshThrottle || DEFAULT_THROTTLE
  });

  const toggleSessionExpiredModal = (): void => {
    setSessionTimeOutModalOpen(false);
    onLogoutRedirection();
  };
  const toggleSessionExpirationNotificationModal = (): void => {
    loginService
      .refreshToken()
      .then(() => auth.persistToken())
      .catch(() => toggleSessionExpiredModal())
      .finally(() => {
        activate();
        setSessionTimeOutNotification(false);
      });
  };

  return (
    <>
      <Modal
        isOpen={sessionTimeOutNotification}
        toggle={toggleSessionExpirationNotificationModal}
        container={window?.document?.getElementById('iso') || undefined}
      >
        <ModalHeader toggle={toggleSessionExpirationNotificationModal}>
          {t(Keys.titleIsAboutToExpire)}
        </ModalHeader>
        <ModalBody>
          <div>{t(Keys.sessionIsAboutToExpire)}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleSessionExpirationNotificationModal}>
            {t(Keys.buttonRefresh)}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={sessionTimeOutModalOpen}
        toggle={toggleSessionExpiredModal}
        container={window?.document?.getElementById('iso') || undefined}
      >
        <ModalHeader toggle={toggleSessionExpiredModal}>{t(Keys.titleExpired)}</ModalHeader>
        <ModalBody>
          <div>{t(Keys.sessionExpiredText)}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleSessionExpiredModal}>
            {t(Keys.buttonExpired)}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
});

export default SessionTimeOutModal;
